import { createSlice } from '@reduxjs/toolkit';
import { DynamoConnector } from '../../DynamoConnector';
import { SORT_MAP } from './leaderboardSlice';
import { ALL_DECKS } from '../../archetypeOptions';
import { nextPageCursor } from './listPaging';

export const archetypesSlice = createSlice({
    name: 'archetypes',
    initialState: {
        listItems: [],
        nextCursor: null,
        isFetching: false,
        loadingState: `idle`,
        // The picked archetype. The route's own fetch loads every deck, so
        // the picker opens on the same thing the list is showing.
        filters: ALL_DECKS,
        sortBy: SORT_MAP.POWERLEVEL,
        sortDirection: SORT_MAP.DIRECTION.DESC,
    },
    reducers: {
        setArchetypesItems: (state, action) => {
            state.listItems = action.payload.map((item) => {
                return {
                    ...item,
                    key: item?.id,
                    storeKey: 'archetypes',
                };
            });
        },
        addNewArchetypesItems: (state, action) => {
            const { cursor, isReload } = action.payload;

            state.nextCursor = cursor || null;
            const newItems = action.payload.items.map((item) => {
                return {
                    ...item,
                    key: item?.id,
                    storeKey: 'archetypes',
                };
            });

            if (!isReload) {
                state.listItems = state.listItems.concat(newItems);
            } else {
                state.listItems = newItems;
            }

            state.isFetching = false;
            state.loadingState = `idle`;
        },
        setArchetypesIsFetching: (state, action) => {
            state.isFetching = action.payload.isFetching;
            state.loadingState = action.payload.loadingState;
        },
        setArchetypesFilters: (state, action) => {
            state.filters = action.payload;
        },
        setArchetypesSortBy: (state, action) => {
            state.sortBy = action?.payload || SORT_MAP.POWERLEVEL;
        },
        setArchetypeSortDirection: (state, action) => {
            state.sortDirection = action?.payload || SORT_MAP.DIRECTION.DESC;
        },
    },
});

export const {
    setArchetypesItems,
    addNewArchetypesItems,
    setArchetypesIsFetching,
    setArchetypesFilters,
    setArchetypesSortBy,
    setArchetypeSortDirection,
} = archetypesSlice.actions;

export const fetchArchetypesItems =
    (cursor, isReload = false, sortBy, sortDirection, filters = {}) =>
    (dispatch, getState) => {
        cursor = cursor !== -1 ? cursor : null;

        if (isReload) {
            dispatch(setArchetypesItems([]));
        }

        dispatch(
            setArchetypesIsFetching({
                isFetching: true,
                loadingState: cursor ? `loadingMore` : `loading`,
            })
        );

        DynamoConnector.getList(cursor, 'decks', sortBy?.index || null, filters, sortDirection, (results) => {
            dispatch(
                addNewArchetypesItems({
                    items: results?.Items || [],
                    cursor: nextPageCursor(results, getState().archetypes.listItems.length),
                    isReload,
                })
            );
        });
    };

/**
 * The route's opening fetch: every deck at the default sort, with the picker
 * and sort controls reset to match so they never show a stale choice.
 */
export const fetchDefaultArchetypeDecks = () => (dispatch) => {
    dispatch(setArchetypesFilters(ALL_DECKS));
    dispatch(setArchetypesSortBy(SORT_MAP.POWERLEVEL));
    dispatch(setArchetypeSortDirection(SORT_MAP.DIRECTION.DESC));
    dispatch(fetchArchetypesItems(null, true, SORT_MAP.POWERLEVEL, SORT_MAP.DIRECTION.DESC, ALL_DECKS));
};

export default archetypesSlice.reducer;
